import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Editor } from 'primereact/editor'
import FormDialog from '../../../components/ui/FormDialog'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import TextInput from '../../../components/ui/TextInput'
import FormDropdownField from '../../../components/ui/form/FormDropdownField'
import FormHtmlEditorField, {
  type HtmlEditorHandle,
} from '../../../components/ui/form/FormHtmlEditorField'
import FormTextField from '../../../components/ui/form/FormTextField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import ParameterPalette from '../../../components/ui/ParameterPalette'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { looksLikeHtml, plainTextToHtml } from '../../../lib/html'
import {
  MAIL_MAX_LEN,
  SUBJECT_MAX_LEN,
  notificationTemplateFormSchema,
  type NotificationTemplateFormValues,
} from './notificationTemplateFormSchema'
import type {
  NotificationTemplate,
  NotificationTypeOption,
  OperationCodeOption,
  TemplateParameterOption,
} from '../types'

/** İçerik alanları — e-posta türünde HTML editörüne dönüşen ikili. */
const CONTENT_FIELDS = ['contentTr', 'contentEn'] as const

/**
 * Panelden kod eklenebilen alanlar: **alıcılar, konu ve içerik** (059-b FR-021a; alıcılar 2026-08-04).
 *
 * <p><b>Alıcı alanları neden dahil:</b> gönderim tarafında alıcı da bağlama göre değişir (ör.
 * aksiyonun sorumlusu, toplantının katılımcıları) — sabit adres yazmak şablonu tek bir kişiye çakar.
 * Bu yüzden Kime/CC/BCC alanları da yer tutucu kabul eder ve **e-posta biçim doğrulaması kaldırıldı**
 * (bkz. `notificationTemplateFormSchema`): `${...}` bir adres değildir, adrese gönderim anında çözülür.
 */
const MAIL_FIELDS = ['mailTo', 'mailCc', 'mailBcc'] as const

const INSERT_FIELDS = [...MAIL_FIELDS, 'subjectTr', 'subjectEn', ...CONTENT_FIELDS] as const
type InsertField = (typeof INSERT_FIELDS)[number]

const EMPTY: NotificationTemplateFormValues = {
  operationCodeId: null,
  notificationTypeId: null,
  subjectTr: '',
  subjectEn: '',
  contentTr: '',
  contentEn: '',
  mailTo: '',
  mailCc: '',
  mailBcc: '',
}

export interface NotificationTemplateFormDialogProps {
  visible: boolean
  /** Düzenlenen kayıt; `null` ise "Yeni ekle". CLOB alanları dolu gelmelidir (tekil uçtan). */
  initial: NotificationTemplate | null
  operationCodes: OperationCodeOption[]
  notificationTypes: NotificationTypeOption[]
  parameters: TemplateParameterOption[]
  parametersError?: boolean
  submitting?: boolean
  onSubmit: (values: NotificationTemplateFormValues) => void
  onHide: () => void
  /**
   * <b>Salt okunur görüntüleme</b> (126 — Kısıtlı Yetkili asistan). Kaydetme düğmesi çizilmez ve
   * metin alanları `readOnly` olur. <b>Her denetim bunu KENDİSİ taşır</b> — `FormDialog`'un
   * fieldset'i artık `disabled` değil (THY AI review HIGH, 26.10.2026: `disabled` metni klavyeyle
   * okunamaz yapıyordu), yani buradaki `readOnly={readOnly}` geçişleri dekoratif değil, yazma
   * yasağının ta kendisidir. Yerel denetimlerin ötesindekiler ayrıca çevrilir: açılır listeler
   * (PrimeReact `Dropdown` tıklamayı kendisi dinler) seçili değeri gösteren `readOnly` metin
   * kutusuna, HTML editörü (Quill `contenteditable`) `readOnly` editöre döner; parametre paneli
   * yalnız yazma yardımcısı olduğu için hiç çizilmez.
   */
  readOnly?: boolean
}

/**
 * Salt okunur e-posta içeriği — PrimeReact `Editor` `readOnly`, araç çubuğu gizli. Düzenleme kipindeki
 * editörle aynı Quill işleyicisinden geçer (aynı `.ui-html-editor` görünümü); yeni bir HTML basma yolu
 * açılmaz. Etiket, Quill'in ürettiği öğeye `id` ile bağlanır (`FormHtmlEditorField` ile aynı kalıp).
 *
 * <p>Araç çubuğu `showHeader={false}` ile DEĞİL `hidden` ile kapatılır: PrimeReact `onLoad`'u yalnız
 * Quill'in araç çubuğu modülü varken çağırıyor (ölçüldü — `showHeader={false}` iken etiket bağı hiç
 * kurulmuyordu). Çubuk DOM'da kalır ama görünmez ve erişilebilirlik ağacının dışındadır.
 */
function ReadOnlyHtmlField({ name, label, value }: Readonly<{ name: string; label: string; value: string }>) {
  return (
    <FormField label={label} htmlFor={name} full>
      <div className="ui-html-editor">
        <Editor
          value={value}
          readOnly
          pt={{ toolbar: { hidden: true } }}
          onLoad={(instance: { root: HTMLElement }) => {
            instance.root.id = name
            instance.root.setAttribute('aria-label', label)
            // Quill `readOnly` kipte kökü `contenteditable="false"` yapar ve o öğe odaklanabilir
            // OLMAKTAN çıkar: klavye-only kullanıcı içerik alanına hiç odaklanamadığı için kutuya
            // sığmayan uzun HTML'i Tab/yön tuşlarıyla kaydırarak OKUYAMIYORDU.
            //
            // Bu, metin alanlarında `disabled` yerine `readOnly` kullanarak kazandığımız erişimin
            // HTML dalında eksik kalmasıydı (THY AI review HIGH, 26.10.2026 — ikinci tur). `tabIndex=0`
            // kökü odak sırasına geri koyar; `aria-label` yukarıda zaten veriliyor, yani erişilebilir
            // ad korunur. Görünür odak halkası `.ui-html-editor` CSS'inde.
            instance.root.tabIndex = 0
          }}
        />
      </div>
    </FormField>
  )
}

/**
 * Bildirim Şablonu ekle/düzenle formu (059 US2).
 *
 * **Üç davranışsal kural burada gerçekleşir:**
 *
 * 1. **Mail alanı ↔ tür kuplajı (FR-021):** Seçili türün sunucudan gelen `email` bayrağı `false` ise
 *    Kime/CC/BCC alanları **hiç gösterilmez ve temizlenir**. Bayrak sunucuda hesaplandığı için
 *    istemcide `60` sihirli sayısı yoktur. Sunucu ayrıca aynı garantiyi yeniden uygular (e-posta dışı
 *    türlerde bu kolonlar `null` yazılır) — dolayısıyla alan gizliyken RHF'de kalan bir değer
 *    kazayla kaydedilse bile veritabanına geçemez.
 *
 *    <p><b>Neden gizleme, neden devre dışı bırakma değil</b> (2026-08-03): devre dışı bir alan
 *    "doldurulacak bir şey var ama şu an olmaz" der; oysa SMS/PN/Web kanalında alıcı listesi
 *    <b>hiç anlam taşımaz</b>. Alanı kaldırmak formu kısaltır ve yanlış beklenti üretmez.
 * 2. **Panelden imleç konumuna ekleme (FR-021a):** Panelden bir koda tıklandığında kod, **son
 *    odaklanılan alanın imleç konumuna** eklenir — hedef **alıcı (Kime/CC/BCC)**, **konu** ya da
 *    **içerik** alanları olabilir. Mevcut metin bozulmaz ve imleç eklenen kodun sonuna taşınır. Panel
 *    **doğrulama yapmaz**.
 * 3. **İçerik biçimi ↔ tür kuplajı (FR-018):** E-posta türünde içerik **HTML**'dir ve zengin metin
 *    editörüyle yazılır; diğer kanallarda (SMS/PN/Web) düz metin alanı kullanılır. Konu her zaman düz
 *    metindir — e-posta konusu HTML taşımaz.
 */
export default function NotificationTemplateFormDialog({
  visible,
  initial,
  operationCodes,
  notificationTypes,
  parameters,
  parametersError,
  submitting,
  onSubmit,
  onHide,
  readOnly = false,
}: Readonly<NotificationTemplateFormDialogProps>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setValue, getValues, watch } = useZodForm(
    notificationTemplateFormSchema,
    { defaultValues: EMPTY },
  )

  /** Panelden ekleme hedefi: son odaklanılan alan (varsayılan TR içerik). */
  const target = useRef<InsertField>('contentTr')

  /**
   * Alan başına son bilinen imleç konumu. Panel butonuna tıklandığında odak zaten alandan çıkmış olur,
   * bu yüzden konum **blur anında** kaydedilir. Kayıt yoksa (alana hiç girilmedi) kod metnin **sonuna**
   * eklenir — odaklanılmamış bir alanda `selectionStart` `null` değil **`0`** olduğundan bu davranış
   * `?? length` ile sağlanamaz; kayıt olmaması bilinçli olarak "sona ekle" anlamına gelir.
   */
  const caret = useRef<Partial<Record<InsertField, { start: number; end: number }>>>({})

  /** Etkin HTML editörlerinin tutamakları (yalnız e-posta türünde dolu olur). */
  const editors = useRef(new Map<string, HtmlEditorHandle>())

  const notificationTypeId = watch('notificationTypeId')
  const emailSelected = notificationTypes.find((n) => n.id === notificationTypeId)?.email ?? false

  useEffect(() => {
    if (!visible) return
    target.current = 'contentTr'
    caret.current = {}
    reset(
      initial
        ? {
            operationCodeId: initial.operationCodeId,
            notificationTypeId: initial.notificationTypeId,
            subjectTr: initial.subjectTr ?? '',
            subjectEn: initial.subjectEn ?? '',
            contentTr: initial.contentTr ?? '',
            contentEn: initial.contentEn ?? '',
            mailTo: initial.mailTo ?? '',
            mailCc: initial.mailCc ?? '',
            mailBcc: initial.mailBcc ?? '',
          }
        : EMPTY,
    )
  }, [visible, initial, reset])

  /**
   * Tür **e-postadan e-posta dışına alındığında** alıcı alanları temizlenir (FR-021).
   *
   * İki koruma bilinçlidir ve ikisi de gerçek veri kaybını önler:
   *
   * 1. **Önceki değer izlenir** — `reset()` ile yapılan ilk doldurma da `notificationTypeId`'yi
   *    değiştirir. "Kullanıcı dokundu" bayrağı bunu ilk doldurmadan ayırt edemiyordu (bayrak
   *    `reset`'in tetiklediği değişimde de kalkıyordu) ve düzenlenen kaydın alıcıları siliniyordu.
   *    Artık yalnız `was → current` **gerçek geçişinde** çalışır.
   * 2. **Seçenekler yüklenmeden hiçbir şey temizlenmez** — liste boşken `emailSelected` daima
   *    `false` olur; bu, "e-posta değil" değil "**henüz bilinmiyor**" demektir. Bu durumda temizlemek,
   *    yavaş ağda düzenleme formunu açmanın alıcıları silmesi anlamına gelirdi.
   */
  const prevTypeId = useRef<number | null>(null)
  useEffect(() => {
    if (!visible) {
      prevTypeId.current = null
      return
    }
    const was = prevTypeId.current
    prevTypeId.current = notificationTypeId

    if (notificationTypes.length === 0) return // tür bilgisi henüz yok → karar verme
    if (was == null || was === notificationTypeId) return // ilk doldurma ya da değişim yok

    const wasEmail = notificationTypes.find((n) => n.id === was)?.email ?? false
    if (wasEmail && !emailSelected) {
      setValue('mailTo', '')
      setValue('mailCc', '')
      setValue('mailBcc', '')
    }
  }, [visible, notificationTypeId, notificationTypes, emailSelected, setValue])

  /**
   * E-posta moduna geçildiğinde **düz metin içerik HTML'e yükseltilir** (059-b).
   *
   * <p>Gerekçe: Quill, kendisine verilen değeri HTML sayar; düz metindeki satır sonları HTML'de anlam
   * taşımadığı için editör açılırken **sessizce tek paragrafa** dönüşür ve kullanıcı satırlarını
   * kaybeder. Yükseltme `shouldDirty: false` ile yapılır — biçim değişikliği kullanıcının yaptığı bir
   * değişiklik değildir, dolayısıyla formu "kirli" göstermemelidir.
   *
   * <p>Ters yön (**e-posta dışına** geçiş) bilinçli olarak dönüştürMEZ: HTML'i düz metne indirmek
   * biçimlendirmeyi geri alınamaz biçimde siler. Kullanıcı düz metin alanında HTML kaynağını görür ve
   * ne yapacağına kendisi karar verir (türü geri alması yeterlidir).
   *
   * <p><b>Tür, render sırasında hesaplanan {@code emailSelected}'tan DEĞİL, etkinin içinde formdan
   * okunur</b> (kullanıcı bildirimi 24.09.2026). Sebebi ölçüldü: yeni bir kayıt açıldığında kaydı
   * forma yazan etki ({@code reset}) bu etkiden ÖNCE çalışır, ama {@code emailSelected} aynı
   * commit'te hâlâ <b>önceki</b> kaydın türünü taşır — e-posta şablonu açılıp kapatıldıktan hemen
   * sonra açılan bir PN şablonunun düz metni de HTML'e yükseltiliyor ve kullanıcı içerik alanında
   * {@code <p>} etiketleri görüyordu. Doğrudan PN açıldığında sorun görünmüyordu, çünkü o commit'te
   * önceki tür de e-posta değildi.
   */
  useEffect(() => {
    if (!visible) return
    // Tür formun GÜNCEL değerinden: `reset` bu etkiden önce çalıştığı için burada yeni kaydın türü
    // okunur; render anındaki `emailSelected` ise bir commit geriden gelir.
    const currentTypeId = getValues('notificationTypeId')
    const isEmail = notificationTypes.find((n) => n.id === currentTypeId)?.email ?? false
    if (!isEmail) return
    for (const field of CONTENT_FIELDS) {
      const value = getValues(field)
      if (value && !looksLikeHtml(value)) {
        setValue(field, plainTextToHtml(value), { shouldDirty: false })
      }
    }
    // `initial` bağımlılıkta: kayıttan kayda geçişte (aynı diyalog açıkken) yeniden değerlendirilmeli.
  }, [visible, initial, emailSelected, notificationTypes, getValues, setValue])

  /**
   * Tür e-postadan başka bir şeye çevrilince alıcı alanları (Kime/CC/BCC) formdan kalkar. Panel hedefi
   * hâlâ onlardan birini gösteriyorsa panele basılan kod <b>görünmeyen</b> bir alana yazılırdı: buton ölü
   * görünür, yazılan kodlar tür yeniden e-postaya çevrilince Kime'de belirirdi. Hedefi güvenli bir alana
   * geri alıp o alanların imleç kaydını düşürüyoruz.
   */
  useEffect(() => {
    if (emailSelected) return
    const mailFields: readonly string[] = MAIL_FIELDS
    if (!mailFields.includes(target.current)) return
    target.current = 'contentTr'
    for (const field of MAIL_FIELDS) {
      delete caret.current[field]
    }
  }, [emailSelected])

  /** Editör hazır olduğunda tutamağını saklar; kaldırıldığında (tür değişimi) siler. */
  const handleEditorReady = useCallback((name: string, handle: HtmlEditorHandle | null) => {
    if (handle) editors.current.set(name, handle)
    else editors.current.delete(name)
  }, [])

  /**
   * Kodu, **son odaklanılan alanın imleç konumuna** ekler.
   *
   * <p>İki yol vardır çünkü iki farklı kontrol türü hedeflenebilir:
   *
   * <ul>
   *   <li><b>HTML editörü</b> (e-posta içeriği): ekleme Quill API'siyle yapılır. RHF değerine yazmak
   *       burada işe yaramaz — PrimeReact `Editor`, dış değer değişimini yalnız editör odakta
   *       <b>değilken</b> uygular.</li>
   *   <li><b>Düz metin alanı</b> (konu, e-posta dışı içerik): değer dizge olarak dilimlenir.</li>
   * </ul>
   */
  const insertAtCursor = useCallback(
    (code: string) => {
      const field = target.current

      const editor = editors.current.get(field)
      if (editor) {
        editor.insert(code)
        return
      }

      const current = (watch(field) as string | undefined) ?? ''
      const saved = caret.current[field]
      // Kayıtlı imleç yoksa alana hiç girilmemiştir → sona ekle.
      const start = saved?.start ?? current.length
      const end = saved?.end ?? current.length
      const next = current.slice(0, start) + code + current.slice(end)
      setValue(field, next, { shouldDirty: true })

      const nextCaret = start + code.length
      caret.current[field] = { start: nextCaret, end: nextCaret }

      // İmleci eklenen kodun sonuna taşı (React değeri yazdıktan sonra).
      const el = document.getElementById(field) as HTMLInputElement | HTMLTextAreaElement | null
      // `setSelectionRange` kontrolü savunmadır: bu yol yalnız düz metin alanları için tasarlandı,
      // ama editör henüz yüklenmemişken aynı `id` bir `contenteditable`'a düşerse çökmemeli.
      if (el && typeof el.setSelectionRange === 'function') {
        requestAnimationFrame(() => {
          el.focus()
          el.setSelectionRange(nextCaret, nextCaret)
        })
      }
    },
    [setValue, watch],
  )

  /**
   * Panelin ekleme hedefini ve imleç konumunu, kapsayıcıya takılan **yerel** `focusin`/`focusout`
   * dinleyicileriyle izler. Bir **callback ref**'tir: dinleyici düğüm DOM'a girdiği anda takılır.
   *
   * <p>Neden JSX `onFocus`/`onBlur` değil: `onFocus`/`onBlur` taşıyan **statik** bir `<div>`
   * Sonar'da `typescript:S6848` açıyor ve bu kural **RELIABILITY** etkisi taşıdığı için THY'nin
   * `/release` kapısının tek ölçütünü (Reliability Rating = A) tehdit ediyor. Kapsayıcıya `role`
   * vermek çözmüyor, **ölçüldü**: `role="group"`, `role="form"` ve `<fieldset>` bulguyu yalnız
   * kardeş kurala (`S6847`) taşıyor; `role="presentation"` susturuyor ama bir `<div>`'in zaten
   * olmayan rolünü sıfırladığı için semantik olarak no-op — yani saf kural kandırması.
   *
   * <p>Davranış **birebir aynıdır**: React'in `onFocus`/`onBlur`'ü zaten yerel `focusin`/`focusout`
   * üzerine kuruludur (ikisi de DOM'da kabarır). Kod artık ne yaptığını da doğru söylüyor —
   * kapsayıcı hiçbir şeyle *etkileşmiyor*, iç alanların odağını *gözlüyor*.
   *
   * <p>⚠️ **`useEffect` ile YAPILMAZ — denendi, 4 test kırıldı.** PrimeReact diyaloğu içeriğini
   * portal + geçiş üzerinden bastığı için efekt koştuğunda `ref.current` henüz **boştur** ve
   * dinleyici hiç takılmaz; hedef alan güncellenmeyince panelden eklenen kod varsayılan alana
   * gider. Callback ref bu zamanlama sorusunu tamamen ortadan kaldırır.
   */
  const formHost = useCallback((host: HTMLDivElement | null) => {
    if (!host) return

    // Bir alan odaklandığında ekleme hedefini günceller. Quill'in `contenteditable` öğesi de `id`
    // taşıdığı için (bkz. `FormHtmlEditorField`) aynı yoldan yakalanır.
    const onFocusIn = (e: FocusEvent) => {
      const id = (e.target as HTMLElement | null)?.id
      if (id && (INSERT_FIELDS as readonly string[]).includes(id)) {
        target.current = id as InsertField
      }
    }

    // Odak alandan çıkarken imleç konumunu saklar — panel tıklaması bundan sonra gelir.
    // `selectionStart` yalnız metin girdilerinde sayıdır; `contenteditable` (HTML editörü) için
    // `undefined` gelir → o alanlarda konum saklanMAZ (editör kendi aralığını zaten izler).
    const onFocusOut = (e: FocusEvent) => {
      const el = e.target as unknown as HTMLInputElement | HTMLTextAreaElement | null
      if (!el || !(INSERT_FIELDS as readonly string[]).includes(el.id)) return
      if (typeof el.selectionStart !== 'number') return
      caret.current[el.id as InsertField] = {
        start: el.selectionStart,
        end: el.selectionEnd ?? el.selectionStart,
      }
    }

    host.addEventListener('focusin', onFocusIn)
    host.addEventListener('focusout', onFocusOut)
    // React 19: callback ref'ten dönen fonksiyon düğüm ayrılırken çağrılır.
    return () => {
      host.removeEventListener('focusin', onFocusIn)
      host.removeEventListener('focusout', onFocusOut)
    }
  }, [])

  let header = t('admin.notificationTemplates.form.createTitle')
  if (readOnly) header = t('admin.notificationTemplates.form.viewTitle')
  else if (initial) header = t('admin.notificationTemplates.form.editTitle')

  // Salt okunurda açılır listelerin yerini alan pasif kutuların metni: seçenek listesi henüz
  // gelmediyse kaydın kendi taşıdığı ad gösterilir.
  const operationCodeId = watch('operationCodeId')
  const operationCodeName =
    operationCodes.find((c) => c.id === operationCodeId)?.name ?? initial?.operationCodeName ?? ''
  const notificationTypeName =
    notificationTypes.find((n) => n.id === notificationTypeId)?.name ??
    initial?.notificationTypeName ??
    ''

  return (
    <FormDialog
      visible={visible}
      header={header}
      submitLabel={t('admin.notificationTemplates.form.save')}
      submitting={submitting}
      onSubmit={handleSubmit(onSubmit)}
      onHide={onHide}
      readOnly={readOnly}
      // Salt okunurda parametre paneli yok → form tek kolona iner, pop-up da daralır.
      width={readOnly ? '46rem' : '62rem'}
    >
      <div
        ref={formHost}
        className="admin-template-form"
        style={readOnly ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}
      >
        <div className="admin-template-form-fields">
          <FormGrid>
            {readOnly ? (
              <>
                <FormField
                  label={t('admin.notificationTemplates.form.operationCode')}
                  htmlFor="operationCodeId"
                >
                  <TextInput id="operationCodeId" value={operationCodeName} readOnly />
                </FormField>
                <FormField
                  label={t('admin.notificationTemplates.form.notificationType')}
                  htmlFor="notificationTypeId"
                >
                  <TextInput id="notificationTypeId" value={notificationTypeName} readOnly />
                </FormField>
              </>
            ) : (
              <>
                <FormDropdownField
                  control={control}
                  name="operationCodeId"
                  label={t('admin.notificationTemplates.form.operationCode')}
                  options={operationCodes.map((c) => ({ label: c.name, value: c.id }))}
                  required
                  filter
                />
                <FormDropdownField
                  control={control}
                  name="notificationTypeId"
                  label={t('admin.notificationTemplates.form.notificationType')}
                  options={notificationTypes.map((n) => ({ label: n.name, value: n.id }))}
                  required
                />
              </>
            )}
            {/*
              Alıcı alanları **yalnız "Eposta" türünde vardır** (FR-021) — e-posta dışı kanalda alıcı
              listesi anlamsızdır, bu yüzden devre dışı bir alan olarak da durmaz.

              Yerleri bilinçli olarak **tür seçiminin hemen altındadır**: (1) belirip kaybolan alanlar,
              koşulu belirleyen kontrolün yanında olmalı — formun dibinde olsalar tür değiştirildiğinde
              görünür alanın dışında belirirlerdi; (2) sıra böylece bir e-posta yazma akışına oturur:
              Kime/CC/BCC → Konu → İçerik.

              Alıcı ve konu alanları `full` ile **kendi satırlarını** kaplar (2026-08-04): artık yer
              tutucu da alıyorlar → değerler uzun ve okunması gereken metinlere dönüştü; iki kolona
              sıkışmış hâlde `${...}` kodları satır ortasında kırpılıyordu.
            */}
            {emailSelected && (
              <>
                <FormTextField
                  readOnly={readOnly}
                  control={control}
                  name="mailTo"
                  label={t('admin.notificationTemplates.form.mailTo')}
                  full
                  maxLength={MAIL_MAX_LEN}
                  placeholder={t('admin.notificationTemplates.form.mailPlaceholder')}
                />
                <FormTextField
                  readOnly={readOnly}
                  control={control}
                  name="mailCc"
                  label={t('admin.notificationTemplates.form.mailCc')}
                  full
                  maxLength={MAIL_MAX_LEN}
                />
                <FormTextField
                  readOnly={readOnly}
                  control={control}
                  name="mailBcc"
                  label={t('admin.notificationTemplates.form.mailBcc')}
                  full
                  maxLength={MAIL_MAX_LEN}
                />
              </>
            )}
            {/* Konu daima DÜZ METİNDİR (e-posta konusu HTML taşımaz), ama parametre alabilir. */}
            <FormTextField
              readOnly={readOnly}
              control={control}
              name="subjectTr"
              label={t('admin.notificationTemplates.form.subjectTr')}
              required
              full
              maxLength={SUBJECT_MAX_LEN}
            />
            <FormTextField
              readOnly={readOnly}
              control={control}
              name="subjectEn"
              label={t('admin.notificationTemplates.form.subjectEn')}
              full
              maxLength={SUBJECT_MAX_LEN}
            />
            {/* İçerik biçimi türe bağlıdır: e-postada HTML editörü, diğerlerinde düz metin. */}
            {emailSelected && readOnly && (
              <>
                <ReadOnlyHtmlField
                  name="contentTr"
                  label={t('admin.notificationTemplates.form.contentTr')}
                  value={watch('contentTr')}
                />
                <ReadOnlyHtmlField
                  name="contentEn"
                  label={t('admin.notificationTemplates.form.contentEn')}
                  value={watch('contentEn')}
                />
              </>
            )}
            {emailSelected && !readOnly && (
              <>
                <FormHtmlEditorField
                  control={control}
                  name="contentTr"
                  label={t('admin.notificationTemplates.form.contentTr')}
                  required
                  onReady={handleEditorReady}
                />
                <FormHtmlEditorField
                  control={control}
                  name="contentEn"
                  label={t('admin.notificationTemplates.form.contentEn')}
                  onReady={handleEditorReady}
                />
              </>
            )}
            {!emailSelected && (
              <>
                <FormTextareaField
                  readOnly={readOnly}
                  control={control}
                  name="contentTr"
                  label={t('admin.notificationTemplates.form.contentTr')}
                  required
                  rows={6}
                />
                <FormTextareaField
                  readOnly={readOnly}
                  control={control}
                  name="contentEn"
                  label={t('admin.notificationTemplates.form.contentEn')}
                  rows={6}
                />
              </>
            )}
          </FormGrid>
        </div>

        {!readOnly && (
          <ParameterPalette
            title={t('admin.notificationTemplates.form.paletteTitle')}
            items={parameters}
            onInsert={insertAtCursor}
            insertLabel={t('admin.notificationTemplates.form.paletteInsert')}
            filterLabel={t('admin.notificationTemplates.form.paletteFilterLabel')}
            filterAllLabel={t('admin.notificationTemplates.form.paletteFilterAll')}
            errorMessage={
              parametersError ? t('admin.notificationTemplates.form.paletteError') : undefined
            }
            emptyMessage={t('admin.notificationTemplates.form.paletteEmpty')}
          />
        )}
      </div>
    </FormDialog>
  )
}
